import React, { useEffect, useState } from "react";
import {
  Button,
  IconButton,
  Box,
  Typography,
  SvgIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from "@mui/material/styles";
import {
  GoogleAuthProvider,
  signInWithRedirect,
  getRedirectResult,
} from "firebase/auth";
import { auth, db } from "../api/firebase";
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  serverTimestamp,
  addDoc,
  collection,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";

// ===============================
// 追加：missing initial state 検知 + 外部ブラウザ導線用
// ===============================
const isMissingInitialStateError = (err) => {
  const msg = (err?.message || "").toLowerCase();
  return (
    msg.includes("missing initial state") ||
    msg.includes("sessionstorage") ||
    msg.includes("storage-partitioned")
  );
};

const isAndroid = () => /Android/i.test(navigator.userAgent || "");
const isIOS = () => /iPhone|iPad|iPod/i.test(navigator.userAgent || "");

const buildChromeIntentUrl = (url) => {
  // Android専用：Chromeで開く intent
  try {
    const u = new URL(url);
    const scheme = u.protocol.replace(":", "");
    const hostAndPath = `${u.host}${u.pathname}${u.search}${u.hash}`;
    return `intent://${hostAndPath}#Intent;scheme=${scheme};package=com.android.chrome;end`;
  } catch {
    return null;
  }
};

// Googleアイコン
const GoogleIcon = () => (
  <SvgIcon viewBox="0 0 48 48" style={{ display: "block" }}>
    <path
      fill="#EA4335"
      d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
    />
    <path
      fill="#4285F4"
      d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
    />
    <path
      fill="#FBBC05"
      d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
    />
    <path
      fill="#34A853"
      d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
    />
  </SvgIcon>
);

// Google ログインボタン
const StyledGoogleLoginButton = styled(Button)({
  color: "#5F6368",
  backgroundColor: "white",
  boxShadow: "none",
  border: "1px solid #dadce0",
  borderRadius: "24px",
  fontWeight: "bold",
  fontSize: "0.875rem",
  fontFamily: '"Google Sans", Roboto, Arial, sans-serif',
  textTransform: "none",
  height: "40px",
  "&:hover": {
    boxShadow: "0 1px 3px rgb(0 0 0 / 30%)",
    backgroundColor: "#f5f5f5",
  },
  "& .MuiSvgIcon-root": {
    width: "24px",
    height: "24px",
  },
  "& .MuiButton-startIcon": {
    margin: 0,
    marginRight: "10px",
  },
});

// モーダルの枠
const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: "16px",
};

const LoginModalContent = ({ onClose, open }) => {
  const navigate = useNavigate();
  const [supportDialogOpen, setSupportDialogOpen] = useState(false);

  // ✅ 追加：外部ブラウザ導線
  const [externalOpen, setExternalOpen] = useState(false);
  const [externalUrl, setExternalUrl] = useState("");

  // Googleログイン
  const handleGoogleLogin = async () => {
    const provider = new GoogleAuthProvider();
    await signInWithRedirect(auth, provider);
  };

  useEffect(() => {
    let isMounted = true;

    const getRedirectGoogle = async () => {
      if (isMounted && !open) {
        try {
          const result = await getRedirectResult(auth);
          if (result) {
            const user = result.user;
            const inUser = await getDoc(doc(db, "users", user.uid));

            if (inUser.exists()) {
              await updateDoc(doc(db, "users", result.user.uid), {
                isOnline: true,
                isLogin: true,
                lastLogin: serverTimestamp(),
              });

              await addDoc(
                collection(db, "users", result.user.uid, "loginHistory"),
                {
                  uid: result.user.uid,
                  loggedIn: serverTimestamp(),
                  userAgent: navigator.userAgent,
                }
              );
              await addDoc(collection(db, "loginHistory"), {
                uid: result.user.uid,
                loggedIn: serverTimestamp(),
                userAgent: navigator.userAgent,
              });

              let loginCount = await getDoc(
                doc(db, "users", result.user.uid)
              ).then((docSnap) => docSnap.data().loginCount);

              if (isNaN(loginCount) === true) {
                loginCount = 1;
              }
              await updateDoc(doc(db, "users", result.user.uid), {
                loginCount: loginCount + 1,
              });

              navigate("/home");
            } else {
              await setDoc(doc(db, "users", result.user.uid), {
                uid: result.user.uid,
                name: user.displayName,
                email: user.email,
                avatar: user.photoURL,
                loginCount: 1,
                avatarPath: "",
                createdAt: serverTimestamp(),
                isOnline: true,
                isLogin: true,
                isStop: false,
              });

              navigate("/terms", {
                state: {
                  firstName: result._tokenResponse.firstName,
                  lastName: result._tokenResponse.lastName,
                  email: user.email,
                  uid: result.user.uid,
                },
              });
            }
          }
        } catch (error) {
          console.error("認証結果の取得に失敗しました:", error);

          // ✅ ここが本命：画像のエラーだけ、外部ブラウザ導線を出す
          if (isMissingInitialStateError(error)) {
            const url = window.location.href;
            setExternalUrl(url);
            setExternalOpen(true);
            return; // 以降の error 書き込みは任意。まず導線優先
          }

          // それ以外は従来通りログだけ残す
          const errorData = {
            message: error.message,
            stack: error.stack,
            user: null,
          };

          await addDoc(collection(db, "error"), {
            createdAt: serverTimestamp(),
            error: errorData,
          });
        }
      }
    };

    getRedirectGoogle();
    return () => {
      isMounted = false;
    };
  }, [open, navigate]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(externalUrl || window.location.href);
    } catch {
      // clipboard不可は無視
    }
  };

  const handleOpenChrome = () => {
    const url = externalUrl || window.location.href;

    // Android: Chrome intent
    if (isAndroid()) {
      const intent = buildChromeIntentUrl(url);
      if (intent) {
        window.location.assign(intent);
        return;
      }
    }

    // それ以外：新規タブ（OS側でブラウザ選べることが多い）
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <Box sx={style}>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          zIndex: 1,
        }}
      >
        <CloseIcon />
      </IconButton>

      <Typography
        variant="h5"
        component="h2"
        sx={{ textAlign: "center", mb: 2 }}
      >
        MasterMind へようこそ！
      </Typography>

      <Typography
        variant="body2"
        sx={{
          mb: 2.5,
          fontSize: "0.85rem",
          lineHeight: 1.7,
          whiteSpace: "pre-line",
        }}
      >
        {`マスターマインドに入会するには、Gメール（グーぐるアカウント）でのログインが必要になります。`}
      </Typography>

      <StyledGoogleLoginButton
        fullWidth
        onClick={handleGoogleLogin}
        startIcon={<GoogleIcon />}
      >
        Googleで続ける
      </StyledGoogleLoginButton>

      <Typography
        variant="caption"
        sx={{
          mt: 2,
          display: "block",
          fontSize: "0.7rem",
          lineHeight: 1.6,
          color: "rgba(0,0,0,0.7)",
        }}
      >
        ※グーグルアカウントを万が一お持ちで無い方は、
        Gメール作成後に上のボタンからお申し込み下さい。
        <br />
        ※どうしてもグーグルアカウントを作れない、またはログイン出来ない方は、
        以下からお問い合わせ下さい：
        <br />
        <a
          href="mailto:info@mastermindmembers.net"
          style={{ color: "#0a66c2", textDecoration: "underline" }}
        >
          info@mastermindmembers.net
        </a>
      </Typography>

      <Typography
        variant="body2"
        sx={{ mt: 2, textAlign: "center", fontSize: "0.75rem" }}
      >
        続行することでMasterMindの{" "}
        <a
          href="/termsofservice"
          style={{ textDecoration: "none", color: "#0a66c2" }}
          target="_blank"
          rel="noreferrer"
        >
          利用規約
        </a>
        に同意し、{" "}
        <a
          href="/privacypolicy"
          style={{ textDecoration: "none", color: "#0a66c2" }}
          target="_blank"
          rel="noreferrer"
        >
          プライバシーポリシー
        </a>
        を読んだものとみなされます。
      </Typography>

      <Typography
        variant="body2"
        sx={{
          mt: 3,
          textAlign: "center",
          fontSize: "0.8rem",
          textDecoration: "underline",
          color: "#1976d2",
          cursor: "pointer",
        }}
        onClick={() => setSupportDialogOpen(true)}
      >
        ログインの詳しい解説動画はこちら
      </Typography>

      {/* サポート用ダイアログ（既存） */}
      <Dialog
        open={supportDialogOpen}
        onClose={() => setSupportDialogOpen(false)}
      >
        <DialogTitle>ログインサポート</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ whiteSpace: "pre-line", mt: 1 }}>
            {`マスターマインドメンバーズサイトをご利用いただき、ありがとうございます。\n\nログイン方法については、以下の動画をご参照ください。\n`}
          </Typography>

          <ul style={{ paddingLeft: "1rem" }}>
            <li>
              <Typography variant="body2">
                【Googleアカウントの作成およびアプリへのログイン方法】{" "}
                <a
                  href="https://vimeo.com/1044231085/5a53116671?share=copy"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#1976d2", textDecoration: "underline" }}
                >
                  こちらの動画
                </a>
              </Typography>
            </li>
            <li style={{ marginTop: "0.5rem" }}>
              <Typography variant="body2">
                【LINEなど別アプリ内で開かれる画面に関するログイン方法】{" "}
                <a
                  href="https://vimeo.com/1048795097/5f2238c7a1?share=copy://vimeo.com/1048795097?share=copy"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#1976d2", textDecoration: "underline" }}
                >
                  こちらの動画
                </a>
              </Typography>
            </li>
          </ul>

          <Typography variant="body2" sx={{ whiteSpace: "pre-line", mt: 2 }}>
            {`動画をご覧いただいても解決しない場合は、下記のメールアドレスまでご連絡くださいませ。\n\n【お問い合わせ先】\ninfo@mastermindmembers.net\n\n確認でき次第、順次ご対応させていただきます。\nどうぞよろしくお願いいたします。`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSupportDialogOpen(false)}>閉じる</Button>
        </DialogActions>
      </Dialog>

      {/* ✅ 追加：missing initial state の時だけ出る “Chromeで開いて” 導線 */}
      <Dialog
        open={externalOpen}
        onClose={() => setExternalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          別のブラウザ（Chrome / Safari）で開いてください
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            LINEなどのアプリ内ブラウザではログインに必要な保存領域が使えず、この画面が出ることがあります。
            外部ブラウザ（Chrome / Safari）で開き直すと解決することが多いです。
          </Alert>

          <Typography variant="body2" sx={{ mb: 1 }}>
            開き直すURL：
          </Typography>
          <Typography
            variant="body2"
            sx={{ wordBreak: "break-all", fontFamily: "monospace" }}
          >
            {externalUrl}
          </Typography>

          {isIOS() && (
            <Typography variant="body2" sx={{ mt: 2 }}>
              iPhoneの場合：画面下の共有メニューから「Safariで開く」をお試しください。
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCopyLink}>リンクをコピー</Button>
          <Button variant="contained" onClick={handleOpenChrome}>
            {isAndroid() ? "Chromeで開く" : "別のブラウザで開く"}
          </Button>
          <Button onClick={() => setExternalOpen(false)}>閉じる</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LoginModalContent;
